
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import StrategiesList from "@/components/marketplace/StrategiesList";
import { MarketplaceStrategy } from "@/types/strategy-types";
import { getRiskLabel, formatDate, getExchangeLogo } from "@/components/marketplace/utils/marketplace-utils";
import { useAccountPerformance } from "@/hooks/marketplace/useAccountPerformance";

const TopStrategies = () => {
  const [strategies, setStrategies] = useState<MarketplaceStrategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStrategyName, setSelectedStrategyName] = useState<string | null>(null);
  
  // Array of specific strategy IDs to display
  const strategyIds = [
    "61127b50-ed56-4e4f-9583-32b71124139e", // 2000 BlueScaleSolution V2
    "3cdd58d1-2f89-466b-a056-ae77ad957f5a", // BlueScaleSolution 5000
    "c98dcd6f-e7d8-4719-bde9-2dad1588116b"  // 5000 BlueScaleSolution
  ];
  
  useEffect(() => {
    const fetchSpecificStrategies = async () => {
      try {
        setLoading(true);
        
        // Fetch specific strategies by their IDs
        const { data, error } = await supabase
          .from("strategy")
          .select("*")
          .in("id", strategyIds);
          
        if (error) {
          console.error("Error fetching specific strategies:", error);
          return;
        }
        
        // Transform the data to match the MarketplaceStrategy interface
        const transformedStrategies: MarketplaceStrategy[] = data?.map(strategy => ({
          id: strategy.id,
          strategy_name: strategy.name || "Untitled Strategy",
          name: strategy.name || "Untitled Strategy",
          min_investment: strategy.min_investment || 0,
          risk_level: strategy.risk_level || 1,
          exchange: strategy.exchange || "Unknown",
          followers_count: 0, // Initialize to 0, will be updated with actual data from account_performance
          performance_percentage: strategy.expected_monthly_profit || 0,
          performance_since: strategy.verified_since ? new Date(strategy.verified_since).toISOString().split('T')[0] : 
            // Display April 2025 as the performance date as requested
            new Date('2025-04-15').toISOString().split('T')[0],
          expected_monthly_profit: strategy.expected_monthly_profit,
          verified_since: strategy.verified_since,
          is_pro: strategy.plan === 'premium' || strategy.plan === 'pro',
          active: strategy.active,
          plan: strategy.plan,
          description: strategy.description || "Trading strategy",
          win_rate: null,
          max_drawdown: strategy.defined_max_drawdown,
          trades_won: null,
          trades_lost: null,
          total_aum: null,
          total_followers: null
        })) || [];
        
        // Sort the strategies to match the order specified in strategyIds
        const orderedStrategies = strategyIds.map(id => 
          transformedStrategies.find(strategy => strategy.id === id)
        ).filter(strategy => strategy !== undefined) as MarketplaceStrategy[];
        
        setStrategies(orderedStrategies);
      } catch (err) {
        console.error("Error in fetchSpecificStrategies:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSpecificStrategies();
  }, []);

  // Use the account performance hook to get actual performance data
  const { accountPerformance, performanceLoading } = useAccountPerformance(strategies, loading);

  // Update strategies with follower data from account performance
  useEffect(() => {
    if (!performanceLoading && accountPerformance && Object.keys(accountPerformance).length > 0) {
      // Create a copy of strategies and update with performance data
      const updatedStrategies = strategies.map(strategy => {
        const performance = accountPerformance[strategy.id];
        
        // Only update if we have performance data for this strategy
        if (performance) {
          return {
            ...strategy,
            // Set followers_count from performance data's num_followers field
            followers_count: performance.num_followers || strategy.followers_count || 0,
            // Also update other performance metrics while we're at it
            win_rate: performance.win_rate || strategy.win_rate,
            max_drawdown: performance.max_drawdown || strategy.max_drawdown,
            trades_won: performance.num_wins || strategy.trades_won,
            trades_lost: performance.num_losses || strategy.trades_lost,
            total_aum: performance.aum || strategy.total_aum
          };
        }
        return strategy;
      });
      
      setStrategies(updatedStrategies);
    }
  }, [accountPerformance, performanceLoading, strategies]);

  const handleSelectStrategy = (strategyName: string) => {
    setSelectedStrategyName(strategyName);
  };
  
  return (
    <section className="bg-corporate-white py-16 md:py-[23px]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold text-corporate-primary-100 mb-4"
          >
            Top Performing Strategies
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-corporate-primary-75 max-w-2xl mx-auto"
          >
            Discover our top strategies with impressive win rates and proven performance in April 2025. 
            Benefit from carefully developed trading strategies.
          </motion.p>
        </div>

        <div className="flex justify-center mb-12">
          <div className="w-full max-w-[1200px] mx-auto">
            <StrategiesList 
              strategies={strategies} 
              isLoading={loading || performanceLoading} 
              onSelectStrategy={handleSelectStrategy} 
              getRiskLabel={getRiskLabel} 
              formatDate={formatDate} 
              getExchangeLogo={getExchangeLogo}
              accountPerformanceData={accountPerformance}
            />
          </div>
        </div>

        <div className="text-center">
          <div className="max-w-2xl mx-auto mb-12">
            <p className="text-corporate-primary-75 mb-8 text-lg">
              METRADE connects you with external strategy providers and their crypto trading strategies. 
              These trading strategies are exclusively available to METRADE MARKETPLACE customers. 
              The funds always remain in the customer's exchange account, and only the customer has access, 
              giving customers full control over their funds and trading activities.
            </p>
          </div>
          <Link to="/get-started">
            <Button className="bg-corporate-secondary-100 hover:bg-corporate-secondary-75 text-corporate-neutral-100 px-8 py-6 text-lg font-semibold rounded-lg">
              Get Started NOW
              <ArrowRight className="ml-2" strokeWidth={2.5} />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TopStrategies;
